import React from "react";
import { API_BASE } from "../../lib/api";

function fmtBytes(n) {
  if (!n) return "";
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + " KB";
  return (n / 1024 / 1024).toFixed(1) + " MB";
}

function iconFor(mime) {
  if (!mime) return "📄";
  if (mime.startsWith("image/")) return "🖼️";
  if (mime.startsWith("video/")) return "🎬";
  if (mime.includes("pdf")) return "📕";
  if (mime.includes("zip")) return "🗜️";
  if (mime.includes("sheet") || mime.includes("csv")) return "📊";
  if (mime.includes("word") || mime.includes("document")) return "📝";
  return "📄";
}

async function authedDownload(url, suggestedName) {
  const token = localStorage.getItem("token");
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) return;
  const blob = await res.blob();
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = u;
  if (suggestedName) a.download = suggestedName;
  else a.target = "_blank";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(u), 60_000);
}

function useAuthedBlobUrl(fileId) {
  const [src, setSrc] = React.useState(null);
  React.useEffect(() => {
    let revoked = false;
    let url;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const r = await fetch(`${API_BASE}/files/${fileId}/preview`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const b = await r.blob();
        url = URL.createObjectURL(b);
        if (!revoked) setSrc(url);
      } catch (e) {}
    })();
    return () => { revoked = true; if (url) URL.revokeObjectURL(url); };
  }, [fileId]);
  return src;
}

function ImageThumb({ file }) {
  const src = useAuthedBlobUrl(file._id);
  return (
    <div
      className="rounded-md overflow-hidden border border-ink-200 cursor-pointer hover:opacity-90 transition"
      onClick={() => authedDownload(`${API_BASE}/files/${file._id}/preview`)}
      title={file.name}
    >
      {src ? (
        <img src={src} alt={file.name} className="max-w-[280px] max-h-[200px] object-cover" />
      ) : (
        <div className="w-[200px] h-[120px] bg-ink-100 flex items-center justify-center text-ink-400">Loading...</div>
      )}
    </div>
  );
}

function VideoPlayer({ file }) {
  const src = useAuthedBlobUrl(file._id);
  return (
    <div className="rounded-md overflow-hidden border border-ink-200 bg-black" title={file.name}>
      {src ? (
        <video src={src} controls className="max-w-[320px] max-h-[240px] block" />
      ) : (
        <div className="w-[280px] h-[160px] flex items-center justify-center text-ink-400 text-xs">Loading video…</div>
      )}
    </div>
  );
}

function PdfPreview({ file }) {
  const [open, setOpen] = React.useState(false);
  const src = useAuthedBlobUrl(open ? file._id : null);
  return (
    <div className="rounded-md border border-ink-200 overflow-hidden bg-surface" title={file.name}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-2 w-full hover:bg-ink-50 transition"
      >
        <span className="text-xl">📕</span>
        <div className="text-left flex-1 min-w-0">
          <div className="text-xs font-semibold text-ink-700 truncate">{file.name}</div>
          <div className="text-[10px] text-ink-400">{fmtBytes(file.size)} · PDF · click to {open ? "hide" : "preview"}</div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); authedDownload(`${API_BASE}/files/${file._id}/download`, file.name); }}
          className="text-[10px] text-blue-600 hover:underline px-2"
        >
          download
        </button>
      </button>
      {open && (
        src
          ? <iframe src={src} title={file.name} className="w-[520px] max-w-full h-[600px] border-t border-ink-200" />
          : <div className="w-[520px] max-w-full h-[100px] flex items-center justify-center text-xs text-ink-400 border-t border-ink-200">Loading…</div>
      )}
    </div>
  );
}

function FileChip({ file }) {
  return (
    <button
      onClick={() => authedDownload(`${API_BASE}/files/${file._id}/download`, file.name)}
      className="flex items-center gap-2 bg-surface border border-ink-200 rounded-md px-3 py-2 hover:bg-ink-50 transition max-w-[280px]"
    >
      <span className="text-xl">{iconFor(file.mime)}</span>
      <div className="text-left flex-1 min-w-0">
        <div className="text-xs font-semibold text-ink-700 truncate">{file.name}</div>
        <div className="text-[10px] text-ink-400">{fmtBytes(file.size)}{file.provider === "gdrive" && " · Drive"}</div>
      </div>
    </button>
  );
}

export default function Attachments({ files }) {
  if (!files || files.length === 0) return null;
  const images = files.filter((f) => (f.mime || "").startsWith("image/"));
  const videos = files.filter((f) => (f.mime || "").startsWith("video/"));
  const pdfs = files.filter((f) => (f.mime || "").includes("pdf"));
  const others = files.filter((f) => {
    const m = f.mime || "";
    return !m.startsWith("image/") && !m.startsWith("video/") && !m.includes("pdf");
  });
  return (
    <div className="space-y-1.5 mt-1.5">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {images.map((f) => <ImageThumb key={f._id} file={f} />)}
        </div>
      )}
      {videos.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {videos.map((f) => <VideoPlayer key={f._id} file={f} />)}
        </div>
      )}
      {pdfs.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {pdfs.map((f) => <PdfPreview key={f._id} file={f} />)}
        </div>
      )}
      {others.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {others.map((f) => <FileChip key={f._id} file={f} />)}
        </div>
      )}
    </div>
  );
}
